import {CanActivateFn} from '@angular/router';
import {inject} from '@angular/core';
import {autoLoginPartialRoutesGuard} from 'angular-auth-oidc-client';
import {ImpersonationService} from '../services/impersonation.service';

export const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';
const POST_LOGIN_REDIRECT_MAX_AGE_MS = 30 * 60 * 1000;

/**
 * Remember a deep link across the Keycloak login. sessionStorage alone is
 * per tab: when the login ends in another tab or in the phone's in-app
 * browser (QR scan from the camera app), the check-in URL was lost and the
 * learner landed on /home without being checked in (#5411). localStorage is
 * the cross-tab fallback, bounded to 30 minutes.
 */
export function rememberPostLoginRedirect(url: string): void {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, url);
    try {
        localStorage.setItem(POST_LOGIN_REDIRECT_KEY, JSON.stringify({ url, at: Date.now() }));
    } catch { /* storage unavailable */ }
}

export function takePostLoginRedirect(): string | null {
    let url = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    try {
        const stored = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
        localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        if (!url && stored) {
            const { url: storedUrl, at } = JSON.parse(stored);
            if (storedUrl && Date.now() - at < POST_LOGIN_REDIRECT_MAX_AGE_MS) url = storedUrl;
        }
    } catch { /* storage unavailable or malformed */ }
    return url;
}

export const impersonationOrAutoLoginGuard: CanActivateFn = (route, state) => {
    const impersonationService = inject(ImpersonationService);
    if (impersonationService.isImpersonating()) {
        return true;
    }
    // Preserve the originally requested URL (incl. query params) so the login
    // flow can return here after Keycloak auth. Without this, the redirect to
    // /login drops the QR check-in params (token/eventId) and no check-in runs,
    // and lesson links from the daily course mail (#5349) end up on /home.
    if (state.url.startsWith('/check-in') || state.url.startsWith('/lesson/')) {
        rememberPostLoginRedirect(state.url);
    }
    return autoLoginPartialRoutesGuard(route, state);
};
